(function () {
  'use strict';

  /* ---------- Fixed header + announcement bar height ---------- (both are position:fixed,
     stacked with the announcement bar on top and the header below it, so both stay visible
     while scrolling in either direction; body needs matching padding-top so page content
     doesn't start underneath them, and their heights change by viewport width/font-loading,
     so they're measured rather than hardcoded). */
  var siteHeader = document.querySelector('.site-header');
  var announcementBar = document.querySelector('.announcement-bar');
  if (siteHeader || announcementBar) {
    var setFixedBarHeights = function () {
      if (announcementBar) {
        document.documentElement.style.setProperty('--announcement-height', announcementBar.offsetHeight + 'px');
      }
      if (siteHeader) {
        document.documentElement.style.setProperty('--header-height', siteHeader.offsetHeight + 'px');
      }
    };
    setFixedBarHeights();
    window.addEventListener('resize', setFixedBarHeights);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(setFixedBarHeights);
    }
  }

  /* ---------- Header search toggle ---------- */
  var searchToggle = document.querySelector('[data-search-toggle]');
  var searchPanel = document.querySelector('[data-search-panel]');
  if (searchToggle && searchPanel) {
    searchToggle.addEventListener('click', function () {
      var willOpen = searchPanel.hidden;
      searchPanel.hidden = !willOpen;
      searchToggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        var input = searchPanel.querySelector('[data-search-input]');
        if (input) input.focus();
      }
    });
    document.addEventListener('click', function (e) {
      if (!searchPanel.hidden && !searchPanel.contains(e.target) && e.target !== searchToggle && !searchToggle.contains(e.target)) {
        searchPanel.hidden = true;
        searchToggle.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !searchPanel.hidden) {
        searchPanel.hidden = true;
        searchToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ---------- Client-side product rendering, bypassing Shopify's collection/all_products
     resolution entirely ----------
     The Wallpapers and Fabrics & Wallpapers collection pages have been serving corrupted
     product cards — verified (via server-side debug output) that Shopify's own collection.products
     and all_products[handle] lookups return the wrong product at render time (which product it
     corrupts changes between identical requests seconds apart with nothing on the theme side
     changing in between — a live bug in Shopify's backend resolution, not something server-side
     Liquid can work around).
     The plain storefront listing endpoint (/products.json) has been reliable and duplicate-free
     every single time it was checked throughout this whole debugging session — it's a different
     code path from collection.products/all_products, so it isn't affected by the same bug. This
     fetches that once, filters by product type client-side, and builds the cards in the browser.
     Genuinely self-updating: add a new wallpaper product in Shopify and it appears automatically,
     no theme edits needed (unlike the old hardcoded-handle-list version this replaced). Covers up
     to 250 products total across the whole store — fine for now, would need real pagination via
     /products.json?page=2 etc. past that. ---------- */
  function formatMoneyValue(cents, format) {
    var value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, value);
  }

  function extractLeadingNumber(title) {
    var m = title.match(/\d+/);
    return m ? parseInt(m[0], 10) : 999999;
  }

  function buildProductCardEl(p, moneyFormat) {
    var sampleVariant = p.variants.filter(function (v) { return /memo|sample/i.test(v.title); })[0] || null;
    var regularVariant = p.variants.filter(function (v) { return v !== sampleVariant; })[0] || p.variants[0];
    var image = (p.images && p.images[0] && p.images[0].src) || '';
    var url = '/products/' + p.handle;

    var card = document.createElement('div');
    card.className = 'product-card';
    card.setAttribute('data-product-type', (p.product_type || '').toLowerCase());
    card.setAttribute('data-has-sample', sampleVariant ? 'true' : 'false');
    card.setAttribute('data-default-url', url);

    var mediaWrap = document.createElement('div');
    mediaWrap.className = 'product-card__media-wrap';

    var mediaLink = document.createElement('a');
    mediaLink.href = url;
    mediaLink.className = 'product-card__media-link';
    mediaLink.setAttribute('data-card-link', '');

    var media = document.createElement('div');
    media.className = 'product-card__media';
    if (image) {
      var img = document.createElement('img');
      img.className = 'product-card__media-img product-card__media-img--primary';
      img.src = image;
      img.loading = 'lazy';
      img.alt = p.title;
      media.appendChild(img);
    } else {
      var placeholder = document.createElement('div');
      placeholder.className = 'product-card__media--placeholder weave-1';
      media.appendChild(placeholder);
    }
    mediaLink.appendChild(media);
    mediaWrap.appendChild(mediaLink);

    if (regularVariant && regularVariant.available) {
      var quickAdd = document.createElement('button');
      quickAdd.type = 'button';
      quickAdd.className = 'product-card__quick-add';
      quickAdd.setAttribute('data-add-variant', regularVariant.id);
      quickAdd.setAttribute('data-mode', 'regular');
      quickAdd.textContent = 'Quick Add';
      mediaWrap.appendChild(quickAdd);
    }
    if (sampleVariant && sampleVariant.available) {
      var sampleBtn = document.createElement('button');
      sampleBtn.type = 'button';
      sampleBtn.className = 'product-card__quick-add product-card__quick-add--sample';
      sampleBtn.setAttribute('data-add-variant', sampleVariant.id);
      sampleBtn.setAttribute('data-mode', 'sample');
      sampleBtn.textContent = 'Add Sample';
      mediaWrap.appendChild(sampleBtn);
    }

    card.appendChild(mediaWrap);

    var body = document.createElement('div');
    body.className = 'product-card__body';

    if (p.product_type) {
      var typeEl = document.createElement('p');
      typeEl.className = 'product-card__type';
      typeEl.textContent = p.product_type;
      body.appendChild(typeEl);
    }

    var titleEl = document.createElement('h3');
    titleEl.className = 'product-card__title';
    var titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.setAttribute('data-card-link', '');
    titleLink.textContent = p.title;
    titleEl.appendChild(titleLink);
    body.appendChild(titleEl);

    // /products.json returns variant prices as decimal strings ("292.38"), not cents — convert
    // to cents so the shared formatMoneyValue helper (used elsewhere with the cents-based
    // /products/<handle>.js format) works the same way here.
    var regularPriceCents = regularVariant ? Math.round(parseFloat(regularVariant.price) * 100) : 0;
    var priceWrap = document.createElement('div');
    priceWrap.className = 'product-card__price product-card__price--regular';
    priceWrap.innerHTML = '<div class="price-block"><span class="price-block__retail">' +
      formatMoneyValue(regularPriceCents, moneyFormat) + '</span></div>';
    body.appendChild(priceWrap);

    if (sampleVariant) {
      var samplePriceCents = Math.round(parseFloat(sampleVariant.price) * 100);
      var samplePriceWrap = document.createElement('div');
      samplePriceWrap.className = 'product-card__price product-card__price--sample';
      samplePriceWrap.innerHTML = '<div class="price-block"><span class="price-block__memo">' +
        formatMoneyValue(samplePriceCents, moneyFormat) + ' <span class="price-block__memo-label">memo sample</span></span></div>';
      body.appendChild(samplePriceWrap);
    }

    card.appendChild(body);
    return card;
  }

  document.querySelectorAll('.product-grid[data-fetch-type]').forEach(function (grid) {
    var types = grid.getAttribute('data-fetch-type').split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    var moneyFormat = grid.getAttribute('data-money-format') || '${{amount}}';

    fetch('/products.json?limit=250')
      .then(function (res) { return res.ok ? res.json() : { products: [] }; })
      .catch(function () { return { products: [] }; })
      .then(function (data) {
      var products = data.products || [];
      var valid = products.filter(function (p) {
        return p && p.variants && p.variants.length > 0 && types.indexOf((p.product_type || '').toLowerCase()) !== -1;
      });
      // De-dupe by id defensively, in case the same product ever appears twice in the feed.
      var seen = {};
      var unique = valid.filter(function (p) {
        if (seen[p.id]) return false;
        seen[p.id] = true;
        return true;
      });
      unique.sort(function (a, b) { return extractLeadingNumber(a.title) - extractLeadingNumber(b.title); });

      grid.innerHTML = '';
      if (unique.length === 0) {
        var empty = document.createElement('p');
        empty.className = 'collection__empty';
        empty.textContent = 'No products in this collection yet.';
        grid.appendChild(empty);
        return;
      }
      unique.forEach(function (p) { grid.appendChild(buildProductCardEl(p, moneyFormat)); });
      bindAddToCartButtons(grid);
    });
  });

  /* ---------- Mobile nav ---------- */
  var navToggle = document.querySelector('[data-mobile-nav-toggle]');
  var mobileNav = document.querySelector('[data-mobile-nav]');
  if (navToggle && mobileNav) {
    navToggle.addEventListener('click', function () {
      var isOpen = !mobileNav.hidden;
      mobileNav.hidden = isOpen;
      navToggle.setAttribute('aria-expanded', String(!isOpen));
    });
  }

  /* ---------- Product gallery: thumbnails, prev/next arrows, and touch swipe ---------- */
  document.querySelectorAll('.product__media').forEach(function (media) {
    var mainWrap = media.querySelector('.product__media-main');
    var mainImg = media.querySelector('[data-product-main-image]');
    var thumbs = media.querySelectorAll('[data-product-thumb]');
    if (!mainWrap || !mainImg || thumbs.length === 0) return;

    var images = Array.prototype.map.call(thumbs, function (t) { return t.getAttribute('data-full'); });
    var index = 0;

    function setActive(i) {
      index = (i + images.length) % images.length;
      // The main image has srcset/sizes for responsive loading; browsers ignore a JS-set
      // .src while srcset is present, so it must be cleared for the swap to actually show.
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = images[index];
      thumbs.forEach(function (t, idx) { t.classList.toggle('is-active', idx === index); });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { setActive(i); });
    });
    thumbs[0].classList.add('is-active');

    if (thumbs.length > 1) {
      var prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'product__media-nav product__media-nav--prev';
      prevBtn.setAttribute('aria-label', 'Previous photo');
      prevBtn.innerHTML = '&larr;';
      prevBtn.addEventListener('click', function () { setActive(index - 1); });

      var nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'product__media-nav product__media-nav--next';
      nextBtn.setAttribute('aria-label', 'Next photo');
      nextBtn.innerHTML = '&rarr;';
      nextBtn.addEventListener('click', function () { setActive(index + 1); });

      mainWrap.appendChild(prevBtn);
      mainWrap.appendChild(nextBtn);

      var startX = null;
      mainWrap.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      mainWrap.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) { setActive(delta < 0 ? index + 1 : index - 1); }
        startX = null;
      }, { passive: true });
    }
  });

  /* ---------- Modals (quote / memo request) ---------- */
  var quoteModal = document.getElementById('quote-request-modal');

  function openModal(modal) {
    if (!modal) return;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }
  function closeModal(modal) {
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-open-quote-modal]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var productField = quoteModal && quoteModal.querySelector('input[name="contact[Product]"]');
      var productInput = quoteModal && quoteModal.querySelector('input[name="contact[Product of interest]"]');
      var title = btn.dataset.productTitle || '';
      if (productField) productField.value = title;
      if (productInput) productInput.value = title;
      openModal(quoteModal);
    });
  });

  document.querySelectorAll('[data-modal-close]').forEach(function (el) {
    el.addEventListener('click', function () {
      closeModal(el.closest('.modal'));
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      document.querySelectorAll('.modal').forEach(function (m) { closeModal(m); });
    }
  });

  /* ---------- Delete confirmation on saved addresses ---------- */
  document.querySelectorAll('[data-confirm-delete]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      if (!window.confirm('Remove this address?')) e.preventDefault();
    });
  });

  /* ---------- Add to cart (memo samples + yardage) via AJAX Cart API ---------- */
  var cartCountEls = document.querySelectorAll('[data-cart-count]');

  function updateCartCount(count) {
    cartCountEls.forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  function bindAddToCartButton(button) {
    if (button.dataset.addToCartBound) return;
    button.dataset.addToCartBound = 'true';
    button.addEventListener('click', function (e) {
      e.preventDefault();
      var variantId = button.getAttribute('data-add-variant');
      var block = button.closest('.product-form__block');
      var qtyInput = block ? block.querySelector('[data-quantity-input]') : null;
      var minQty = qtyInput ? parseInt(qtyInput.getAttribute('min'), 10) || 1 : 1;
      var quantity = qtyInput ? parseInt(qtyInput.value, 10) || minQty : 1;
      if (quantity < minQty) {
        quantity = minQty;
        if (qtyInput) qtyInput.value = minQty;
      }

      var originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function () {
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          button.textContent = 'Added ✓';
          setTimeout(function () {
            button.textContent = originalText;
            button.disabled = false;
          }, 1800);
        })
        .catch(function (err) {
          button.textContent = (err && err.description) || 'Couldn’t add — try again';
          button.disabled = false;
          setTimeout(function () { button.textContent = originalText; }, 2500);
        });
    });
  }

  function bindAddToCartButtons(scope) {
    (scope || document).querySelectorAll('[data-add-variant]').forEach(bindAddToCartButton);
  }

  bindAddToCartButtons();

  /* ---------- Generic product variant picker (used by Accessories and multi-length Fabric) ---------- */
  document.querySelectorAll('[data-variant-picker]').forEach(function (wrap) {
    var section = wrap.closest('[data-product-section]');
    if (!section) return;

    var variantsEl = section.querySelector('script[data-variant-json]');
    var pricesEl = section.querySelector('script[data-variant-prices-json]');
    var imagesEl = section.querySelector('script[data-variant-images-json]');
    if (!variantsEl || !pricesEl) return;

    var variants = JSON.parse(variantsEl.textContent);
    var prices = JSON.parse(pricesEl.textContent);
    var images = imagesEl ? JSON.parse(imagesEl.textContent) : [];
    var selects = wrap.querySelectorAll('[data-variant-option-index]');
    var variantIdInput = section.querySelector('[data-variant-id-input]');
    var addBtn = section.querySelector('[data-variant-add-btn]');
    var priceEl = section.querySelector('[data-variant-price]');
    var availabilityEl = section.querySelector('[data-variant-availability]');
    var mainImageEl = section.querySelector('[data-product-main-image]');
    var shipMessage = wrap.getAttribute('data-ship-message') || 'In stock, ships within 1–3 business days.';

    function selectedOptions() {
      var values = [];
      selects.forEach(function (select) {
        values[parseInt(select.getAttribute('data-variant-option-index'), 10)] = select.value;
      });
      return values;
    }

    function matchVariant(values) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var opts = [v.option1, v.option2, v.option3].filter(function (o) { return o !== null; });
        var isMatch = opts.every(function (o, idx) { return o === values[idx]; });
        if (isMatch) return { variant: v, price: prices[i], image: images[i] };
      }
      return null;
    }

    function update() {
      var match = matchVariant(selectedOptions());
      if (!match) return;
      var variant = match.variant;
      if (variantIdInput) variantIdInput.value = variant.id;
      if (priceEl) priceEl.textContent = match.price;
      if (addBtn) {
        addBtn.setAttribute('data-add-variant', variant.id);
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }
      if (availabilityEl) {
        availabilityEl.textContent = variant.available ? shipMessage : 'Currently unavailable.';
      }
      if (mainImageEl && match.image) {
        mainImageEl.src = match.image;
      }
    }

    selects.forEach(function (select) {
      select.addEventListener('change', update);
    });
  });

  /* ---------- Related products (native Shopify recommendations) ---------- */
  document.querySelectorAll('[data-related-products]').forEach(function (el) {
    var url = el.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newContent = doc.querySelector('[data-related-products]');
        if (newContent && newContent.querySelector('.product-grid')) {
          el.innerHTML = newContent.innerHTML;
        } else {
          el.remove();
        }
      })
      .catch(function () { el.remove(); });
  });

  /* ---------- Recently viewed (localStorage, no native Shopify API for this) ---------- */
  (function () {
    var STORAGE_KEY = 'callista_recently_viewed';
    var MAX_ITEMS = 8;
    var SHOW_COUNT = 4;

    function getViewed() {
      try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || []; }
      catch (e) { return []; }
    }
    function saveViewed(list) {
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
    }
    function formatMoney(cents, format) {
      var amount = (cents / 100);
      var value = format.indexOf('amount_no_decimals') > -1
        ? Math.round(amount).toString()
        : amount.toFixed(2);
      return format.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, value);
    }

    var section = document.querySelector('[data-recently-viewed]');
    var currentHandle = section ? section.getAttribute('data-current-handle') : null;

    if (currentHandle) {
      var viewed = getViewed().filter(function (h) { return h !== currentHandle; });
      viewed.unshift(currentHandle);
      saveViewed(viewed.slice(0, MAX_ITEMS));
    }

    if (!section) return;
    var grid = section.querySelector('[data-recently-viewed-grid]');
    var moneyFormat = section.getAttribute('data-money-format') || '${{amount}}';
    var toShow = getViewed().filter(function (h) { return h !== currentHandle; }).slice(0, SHOW_COUNT);
    if (!grid || toShow.length === 0) return;

    Promise.all(toShow.map(function (handle) {
      return fetch('/products/' + handle + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var html = '';
      products.forEach(function (p) {
        if (!p) return;
        var image = p.featured_image || (p.images && p.images[0]) || '';
        var title = (p.title || '').replace(/"/g, '&quot;');
        html +=
          '<div class="product-card">' +
            '<div class="product-card__media-wrap">' +
              '<a href="' + p.url + '" class="product-card__media-link">' +
                '<div class="product-card__media">' +
                  (image
                    ? '<img class="product-card__media-img product-card__media-img--primary" src="' + image + '" alt="' + title + '" loading="lazy">'
                    : '<div class="product-card__media--placeholder weave-1"></div>') +
                '</div>' +
              '</a>' +
            '</div>' +
            '<div class="product-card__body">' +
              '<h3 class="product-card__title"><a href="' + p.url + '">' + p.title + '</a></h3>' +
              '<div class="price-block"><span class="price-block__retail">' + formatMoney(p.price, moneyFormat) + '</span></div>' +
            '</div>' +
          '</div>';
      });
      if (html) {
        grid.innerHTML = html;
        section.hidden = false;
      }
    });
  })();

  /* ---------- Collection type/sample filter (Fabrics & Wallpapers combined collection) ---------- */
  document.querySelectorAll('.collection__filters').forEach(function (bar) {
    var section = bar.closest('section');
    var grid = section ? section.querySelector('.product-grid') : null;
    if (!grid) return;
    var buttons = bar.querySelectorAll('.collection__filter');
    var countEl = section.querySelector('.collection__count');

    function applyFilter(type, mode) {
      // Queried fresh on every call, not cached at setup — grids populated asynchronously
      // (e.g. the client-side product fetch on Wallpapers/Fabrics & Wallpapers) have no cards
      // yet at page-load time, so a cached NodeList captured then would always be empty.
      var cards = grid.querySelectorAll('.product-card');
      var isSample = mode === 'sample';
      grid.classList.toggle('is-sample-mode', isSample);
      var visibleCount = 0;
      cards.forEach(function (card) {
        var matchesType = type === 'all' || card.getAttribute('data-product-type') === type;
        card.hidden = !matchesType;

        // Sample mode additionally hides any card with no sample variant via CSS
        // (.is-sample-mode .product-card[data-has-sample="false"]) — factor that into the
        // visible count too, not just the type match, so it reflects what's actually shown.
        var hasSample = card.getAttribute('data-has-sample') === 'true';
        if (matchesType && (!isSample || hasSample)) visibleCount++;

        // In sample mode, point the card at its standalone sample product page (if one is
        // linked via the trade.sample_product metafield) instead of the full product page.
        var defaultUrl = card.getAttribute('data-default-url');
        var sampleUrl = card.getAttribute('data-sample-url');
        if (defaultUrl) {
          var targetUrl = (isSample && sampleUrl) ? sampleUrl : defaultUrl;
          card.querySelectorAll('[data-card-link]').forEach(function (link) {
            link.setAttribute('href', targetUrl);
          });
        }
      });
      if (countEl) {
        countEl.textContent = visibleCount + (visibleCount === 1 ? ' product' : ' products');
      }
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        buttons.forEach(function (b) { b.classList.toggle('is-active', b === btn); });
        applyFilter(btn.getAttribute('data-filter-type'), btn.getAttribute('data-filter-mode'));
      });
    });

    // Arriving via a link with ?type=X (e.g. the mobile Accessories submenu) applies that
    // filter immediately instead of defaulting to "All".
    var urlType = new URLSearchParams(window.location.search).get('type');
    if (urlType) {
      var matchingBtn = bar.querySelector('.collection__filter[data-filter-type="' + urlType + '"]');
      if (matchingBtn) {
        buttons.forEach(function (b) { b.classList.toggle('is-active', b === matchingBtn); });
        applyFilter(matchingBtn.getAttribute('data-filter-type'), matchingBtn.getAttribute('data-filter-mode'));
      }
    }
  });

  /* ---------- Mobile nav accordion (Accessories submenu) ---------- */
  document.querySelectorAll('[data-mobile-submenu-toggle]').forEach(function (toggle) {
    var submenu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!submenu) return;
    toggle.addEventListener('click', function () {
      var willOpen = submenu.hidden;
      submenu.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', String(willOpen));
    });
  });

  /* ---------- Wall area calculator (recommends rolls needed, reuses the existing roll
     add-to-cart button/quantity input rather than its own cart logic) ---------- */
  document.querySelectorAll('[data-wall-calc]').forEach(function (calc) {
    var toggleBtn = calc.querySelector('[data-wall-calc-toggle]');
    var panel = calc.querySelector('[data-wall-calc-panel]');
    var unitButtons = calc.querySelectorAll('[data-wall-calc-unit]');
    var widthInput = calc.querySelector('[data-wall-calc-width]');
    var heightInput = calc.querySelector('[data-wall-calc-height]');
    var calculateBtn = calc.querySelector('[data-wall-calc-calculate]');
    var resultEl = calc.querySelector('[data-wall-calc-result]');
    var countEl = calc.querySelector('[data-wall-calc-count]');
    var sizeEl = calc.querySelector('[data-wall-calc-size]');
    var priceEl = calc.querySelector('[data-wall-calc-price]');
    var addBtn = calc.querySelector('[data-wall-calc-add]');

    var rollWidthCm = parseFloat(calc.getAttribute('data-roll-width-cm'));
    var rollLengthCm = parseFloat(calc.getAttribute('data-roll-length-cm'));
    var rollPriceCents = parseInt(calc.getAttribute('data-roll-price'), 10);
    var moneyFormat = calc.getAttribute('data-money-format') || '${{amount}}';
    var unit = 'cm';
    var recommendedRolls = 0;

    if (!rollWidthCm || !rollLengthCm || !calculateBtn) return;

    function formatMoney(cents) {
      var value = (cents / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, value);
    }

    if (toggleBtn && panel) {
      toggleBtn.addEventListener('click', function () {
        var willShow = panel.hidden;
        panel.hidden = !willShow;
        toggleBtn.setAttribute('aria-expanded', String(willShow));
      });
    }

    unitButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        unit = btn.getAttribute('data-wall-calc-unit');
        unitButtons.forEach(function (b) { b.classList.toggle('is-active', b === btn); });
      });
    });

    calculateBtn.addEventListener('click', function () {
      var width = parseFloat(widthInput.value);
      var height = parseFloat(heightInput.value);
      if (!width || !height || width <= 0 || height <= 0) return;

      var widthCm = unit === 'in' ? width * 2.54 : width;
      var heightCm = unit === 'in' ? height * 2.54 : height;

      var strips = Math.ceil(widthCm / rollWidthCm);
      var rollsPerStrip = Math.ceil(heightCm / rollLengthCm);
      recommendedRolls = Math.max(1, strips * rollsPerStrip);

      if (countEl) countEl.textContent = recommendedRolls;
      if (sizeEl) sizeEl.textContent = rollLengthCm + 'x' + rollWidthCm + ' cm';
      if (priceEl) priceEl.textContent = formatMoney(rollPriceCents * recommendedRolls);
      if (resultEl) resultEl.hidden = false;
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        if (!recommendedRolls) return;
        var block = calc.closest('.product-form__block');
        var qtyInput = block ? block.querySelector('[data-quantity-input]') : null;
        var cartBtn = block ? block.querySelector('[data-add-variant]') : null;
        if (qtyInput) qtyInput.value = recommendedRolls;
        if (cartBtn) cartBtn.click();
      });
    }
  });

  /* ---------- Roll quantity stepper + live Add to Cart price ---------- */
  document.querySelectorAll('[data-roll-qty]').forEach(function (qtyInput) {
    var block = qtyInput.closest('.product-form__block');
    if (!block) return;
    var addBtn = block.querySelector('[data-roll-add-btn]');
    var unitPriceCents = addBtn ? parseInt(addBtn.getAttribute('data-unit-price-cents'), 10) : 0;
    var moneyFormat = block.getAttribute('data-money-format') || '${{amount}}';
    var isAvailable = addBtn && !addBtn.disabled;

    function formatMoney(cents) {
      var value = (cents / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, value);
    }

    function updatePrice() {
      if (!addBtn || !isAvailable || !unitPriceCents) return;
      var qty = parseInt(qtyInput.value, 10) || 1;
      addBtn.textContent = 'Add to Cart — ' + formatMoney(unitPriceCents * qty);
    }

    block.querySelectorAll('[data-qty-step]').forEach(function (stepBtn) {
      stepBtn.addEventListener('click', function () {
        var delta = parseInt(stepBtn.getAttribute('data-qty-step'), 10);
        var min = parseInt(qtyInput.getAttribute('min'), 10) || 1;
        var current = parseInt(qtyInput.value, 10) || min;
        qtyInput.value = Math.max(min, current + delta);
        updatePrice();
      });
    });

    qtyInput.addEventListener('input', updatePrice);
  });

  /* ---------- Cart line quantity updates ---------- */
  document.querySelectorAll('[data-cart-line-qty]').forEach(function (input) {
    input.addEventListener('change', function () {
      var form = input.closest('form');
      if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
    });
  });

  /* ---------- Journal "Next" (mobile): reveals 4 more of the already-rendered cards per
     click; once this page's cards are exhausted, a further click follows the real Shopify
     pagination link to the next page (if one exists) instead of a dead end. ---------- */
  document.querySelectorAll('[data-journal-load-more]').forEach(function (btn) {
    var grid = btn.closest('.journal__grid');
    if (!grid) return;
    btn.addEventListener('click', function () {
      var hidden = grid.querySelectorAll('.journal-card--mobile-hidden');
      if (hidden.length > 0) {
        Array.prototype.slice.call(hidden, 0, 4).forEach(function (card) {
          card.classList.remove('journal-card--mobile-hidden');
        });
        if (grid.querySelectorAll('.journal-card--mobile-hidden').length === 0 && !btn.getAttribute('data-next-url')) {
          btn.hidden = true;
        }
      } else {
        var nextUrl = btn.getAttribute('data-next-url');
        if (nextUrl) window.location.href = nextUrl;
      }
    });
  });
})();
